import React, { useState } from 'react';
import { fmt } from '../../../../lib/formatters';
import { Trash2 } from 'lucide-react';
import TableActionButton from '../../../../components/ui/TableActionButton';

const CustomCategoryItemRow = ({ item, categoryLabel, onChange, onRemove }) => {
  return (
    <div className="flex items-center gap-2 w-full relative z-10">
      <div className="w-16 shrink-0 text-right pr-2">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{categoryLabel}</span>
      </div>
      <div className="relative flex-1">
        <input 
          type="text" 
          value={item.name} 
          onChange={(e) => onChange('name', e.target.value)}
          placeholder={`Enter custom ${categoryLabel.toLowerCase()}...`}
          className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors shadow-sm placeholder:text-slate-300"
        />
      </div>
      <div className="w-24 shrink-0 relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[11px] font-bold">₹</span>
        <input 
          type="number" 
          value={item.amount} 
          onChange={(e) => onChange('amount', e.target.value)}
          placeholder="0"
          className="w-full h-8 text-[12px] font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded pl-5 pr-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 shadow-sm"
        />
      </div>
      <div className="ml-1 shrink-0 scale-75 origin-right">
        <TableActionButton 
          icon={Trash2} 
          variant="danger" 
          onClick={onRemove} 
          title="Remove Item"
        />
      </div>
    </div>
  );
};

import { NormalSearch } from './NormalSearch';
import { HardwareSelectionTable } from './HardwareSelectionTable';

const PriceListItemRow = ({ item, availableItems, onChange, onRemove }) => {
  const uniqueDescriptions = Array.from(new Set(availableItems.map(i => i.description || i.itemName || i.materialDescription || '').filter(Boolean)));
  const uniqueInternalTypes = Array.from(new Set(availableItems.map(i => i.internalType || '').filter(Boolean)));
  const uniqueShutterTypes = Array.from(new Set(availableItems.map(i => i.shutterType || '').filter(Boolean)));

  const handleFieldChange = (field, value) => {
    const updated = { ...item, [field]: value };
    const updates = { [field]: value };
    
    // Auto-calc if we have the critical fields
    if (updated.description && updated.internalType && updated.shutterType && updated.width && updated.height && updated.depth) {
      const match = availableItems.find(ai => 
        (ai.description || ai.itemName || ai.materialDescription || '').toLowerCase().trim() === String(updated.description).toLowerCase().trim() &&
        (ai.internalType || '').toLowerCase().trim() === String(updated.internalType).toLowerCase().trim() &&
        (ai.shutterType || '').toLowerCase().trim() === String(updated.shutterType).toLowerCase().trim() &&
        Number(ai.width) === Number(updated.width) &&
        Number(ai.height) === Number(updated.height) &&
        Number(ai.depth) === Number(updated.depth)
      );
      if (match) {
        updates.amount = match.price || match.mrp || match.purchaseRate || match.rate || 0;
      }
    }
    onChange(updates);
  };

  return (
    <div className="flex flex-col gap-2 w-full bg-white p-3 rounded-md border border-slate-200 relative mb-2 shadow-sm">
      <div className="flex justify-between items-center mb-1">
        <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Price List Item</span>
        <TableActionButton icon={Trash2} variant="danger" onClick={onRemove} title="Remove Item" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase">Description</label>
          <input 
            list="desc-list"
            value={item.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="e.g. Wardrobe"
            className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
          <datalist id="desc-list">{uniqueDescriptions.map(d => <option key={d} value={d} />)}</datalist>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase">Internal Type</label>
          <input 
            list="int-list"
            value={item.internalType || ''}
            onChange={(e) => handleFieldChange('internalType', e.target.value)}
            placeholder="Search internal type..."
            className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
          <datalist id="int-list">{uniqueInternalTypes.map(d => <option key={d} value={d} />)}</datalist>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase">External/Shutter Type</label>
          <input 
            list="shut-list"
            value={item.shutterType || ''}
            onChange={(e) => handleFieldChange('shutterType', e.target.value)}
            placeholder="Search shutter type..."
            className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
          <datalist id="shut-list">{uniqueShutterTypes.map(d => <option key={d} value={d} />)}</datalist>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mt-1">
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase">Width (W)</label>
          <input 
            type="number"
            value={item.width || ''}
            onChange={(e) => handleFieldChange('width', e.target.value)}
            placeholder="0"
            className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase">Height (H)</label>
          <input 
            type="number"
            value={item.height || ''}
            onChange={(e) => handleFieldChange('height', e.target.value)}
            placeholder="0"
            className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase">Depth (D)</label>
          <input 
            type="number"
            value={item.depth || ''}
            onChange={(e) => handleFieldChange('depth', e.target.value)}
            placeholder="0"
            className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
        </div>

        <div className="flex flex-col gap-1">
          {/* 
          <label className="text-[9px] font-bold text-indigo-600 uppercase">Final Price (₹)</label>
          <input 
            type="number"
            value={item.amount || ''}
            onChange={(e) => handleFieldChange('amount', e.target.value)}
            placeholder="0"
            className="w-full h-8 text-[14px] font-black text-slate-800 bg-indigo-50 border border-indigo-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 shadow-sm"
          />
          */}
        </div>
      </div>
    </div>
  );
};

const MeasurementPricingBlock = ({ measurementItem, availableItems, blumStockItems, onUpdate, onRemoveMeasurement }) => {
  const { measurement, priceListItems, blumItems, customItems } = measurementItem;

  const handleItemChange = (category, tempId, updatesOrField, value) => {
    onUpdate({
      ...measurementItem,
      [category]: measurementItem[category].map(i => {
        if (i.tempId === tempId) {
          if (typeof updatesOrField === 'object') {
             return { ...i, ...updatesOrField };
          }
          return { ...i, [updatesOrField]: value };
        }
        return i;
      })
    });
  };

  const handleItemSelect = (category, tempId, name, amount) => {
    onUpdate({
      ...measurementItem,
      [category]: measurementItem[category].map(i => i.tempId === tempId ? { ...i, name, amount } : i)
    });
  };

  const handleAddItem = (category) => {
    onUpdate({
      ...measurementItem,
      [category]: [...(measurementItem[category] || []), { tempId: category + '-' + Date.now() + Math.random(), name: '', description: '', internalType: '', shutterType: '', width: '', height: '', depth: '', amount: 0 }]
    });
  };

  const handleRemoveItem = (category, tempId) => {
    onUpdate({
      ...measurementItem,
      [category]: measurementItem[category].filter(i => i.tempId !== tempId)
    });
  };

  const renderCategory = (categoryKey, label) => {
    const items = measurementItem[categoryKey] || [];

    if (categoryKey === 'blumItems') {
      return (
        <div className="flex flex-col gap-2 w-full mb-4 mt-2">
           {/* Search Input */}
           <div className="flex items-center gap-2 w-full relative z-[60]">
             <div className="w-16 shrink-0 text-right pr-2">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
             </div>
             <NormalSearch 
               selectedItems={items}
               onSelectMulti={(selectedData, checked, rawData) => {
                 if (checked) {
                   onUpdate({
                     ...measurementItem,
                     [categoryKey]: [...items, { tempId: categoryKey + '-' + Date.now() + Math.random(), name: selectedData.name, amount: selectedData.amount, _id: selectedData.id, rawData }]
                   });
                 } else {
                   const existing = items.find(i => i._id === selectedData.id || i.name === selectedData.name);
                   if (existing) {
                      handleRemoveItem(categoryKey, existing.tempId);
                   }
                 }
               }}
             />
           </div>
           {/* Selected Items Table */}
           <HardwareSelectionTable items={items} onRemove={(tempId) => handleRemoveItem(categoryKey, tempId)} />
           
           {/* Hardware Final Price Summary */}
           {items && items.filter(i => i.name && i.name.trim() !== '').length > 0 && (
             <div className="flex justify-end mt-2">
               <div className="flex items-center gap-3">
                 <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Final Price (₹)</span>
                 <input 
                   type="number" 
                   value={measurementItem.hardwareFinalPrice !== undefined ? measurementItem.hardwareFinalPrice : items.filter(i => i.name && i.name.trim() !== '').reduce((sum, item) => {
                     const amt = (item.rawData && item.rawData.netAmount) ? item.rawData.netAmount : (item.amount || 0);
                     return sum + Number(amt);
                   }, 0)}
                   onChange={(e) => {
                     const val = e.target.value;
                     onUpdate({ ...measurementItem, hardwareFinalPrice: val === '' ? '' : (parseFloat(val) || 0) });
                   }}
                   className="w-32 h-9 text-[14px] font-black text-indigo-600 bg-white border border-slate-200 rounded-md px-3 text-right focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 focus:outline-none shadow-sm transition-all hover:border-indigo-300"
                 />
               </div>
             </div>
           )}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1 w-full">
        {items.map((item, idx) => {
          if (categoryKey === 'customItems') {
            return (
              <CustomCategoryItemRow 
                key={item.tempId}
                item={item}
                categoryLabel={idx === 0 ? label : ''}
                onChange={(field, value) => handleItemChange(categoryKey, item.tempId, field, value)}
                onRemove={() => handleRemoveItem(categoryKey, item.tempId)}
              />
            );
          }
          if (categoryKey === 'priceListItems') {
            return (
              <PriceListItemRow 
                key={item.tempId}
                item={item}
                availableItems={availableItems}
                onChange={(updates) => handleItemChange(categoryKey, item.tempId, updates)}
                onRemove={() => handleRemoveItem(categoryKey, item.tempId)}
              />
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 bg-white p-4 rounded-xl border border-slate-200 w-full mb-4 shadow-sm hover:border-indigo-100 transition-all relative">
      {/* Top Row: Measurement Info */}
      <div className="w-full flex flex-col border-b border-slate-100 pb-4">
        {measurement ? (
          <div className="flex flex-wrap gap-x-8 gap-y-4 items-center">
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Zone</span>
              <span className="font-medium text-slate-800 text-[12px]">{measurement.zone || '-'}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Floor</span>
              <span className="font-medium text-slate-800 text-[12px]">{measurement.floor || '-'}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Dimensions</span>
              <span className="font-medium text-slate-800 text-[12px]">H:{measurement.height} × W:{measurement.width} × D:{measurement.depth}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Quantity</span>
              <span className="font-medium text-slate-800 text-[12px]">{measurement.qty || 1}</span>
            </div>
            <div className="flex flex-col gap-1 max-w-[200px]">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Particulars</span>
              <span className="font-medium text-slate-800 text-[12px] truncate" title={measurement.particulars}>{measurement.particulars || '-'}</span>
            </div>
            <div className="flex flex-col gap-1 max-w-[200px]">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Note</span>
              <span className="font-medium text-slate-800 text-[12px] truncate" title={measurement.notes}>{measurement.notes || '-'}</span>
            </div>
          </div>
        ) : (
          <div className="text-[13px] text-slate-500 italic font-medium flex items-center h-full">Custom Quotation Block (No site visit details)</div>
        )}
      </div>

      {/* Bottom Row: Inputs & Actions */}
      <div className="w-full flex flex-col">
        {/* Actions */}
        <div className="flex justify-start flex-wrap gap-2 mb-3">
          <button type="button" onClick={() => handleAddItem('priceListItems')} className="text-[10px] font-bold text-slate-500 hover:text-indigo-600 px-2.5 py-1.5 bg-slate-50 hover:bg-indigo-50 rounded transition-colors shadow-sm">+ Add Item (W/H/D)</button>
          <button type="button" onClick={() => handleAddItem('blumItems')} className="text-[10px] font-bold text-slate-500 hover:text-indigo-600 px-2.5 py-1.5 bg-slate-50 hover:bg-indigo-50 rounded transition-colors shadow-sm">+ Search</button>
          <button type="button" onClick={() => handleAddItem('customItems')} className="text-[10px] font-bold text-slate-700 hover:text-indigo-600 px-2.5 py-1.5 bg-slate-100 hover:bg-indigo-100 rounded transition-colors shadow-sm">+ Custom Item</button>
        </div>

        {/* Categories */}
        <div className="flex flex-col gap-2 bg-slate-50/40 p-3 rounded-lg border border-slate-100">
          {renderCategory('priceListItems', 'Item')}
          {renderCategory('blumItems', 'Search')}
          {renderCategory('customItems', 'Custom')}
        </div>
      </div>
    </div>
  );
};

export default MeasurementPricingBlock;
